import React, { useContext, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { ExecutionDetailOutDTO, ExecutionListItemOutDTO, ExecutionStateEnum } from '@api/ringil-api.v2';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import RingilButton from '@components/ringil3/Buttons/RingilButton';
import InputSelect from '@components/ringil3/Input/InputSelect/InputSelect';
import RingilDataTable, { ColumnConfig, PagedData } from '@components/RingilDataTable/RingilDataTable';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser';
import { stringDateTimeWithLocaleFromApiStringInTz } from '@utils/dateWithTz';
import ExplainTree from '../ExplainTree';
import EntityLink from '../EntityLink';
import { ExecutedAction, executedActionDetail, executedActionError } from '../i18nKeys';
import { useRuleApi } from '../useRuleApi';
import { useTriggerLabel } from './useTriggerLabel';

interface ComponentProps {
  companyId?: string;
  /** Set on a rule's own history; absent for the whole company. */
  ruleId?: string;
}

/** How a run ended, in words a reader can act on. */
const stateLabel = (tr: (key: string, fallback: string) => string, state: ExecutionStateEnum, matched: boolean): { label: string; className: string } => {
  switch (state) {
    case ExecutionStateEnum.EXECUTED:
      return { label: tr('AiDispatcher.stateExecuted', 'Acted'), className: 'bg-green-100 text-green-800' };
    case ExecutionStateEnum.FAILED:
      return { label: tr('AiDispatcher.stateFailed', 'Failed'), className: 'bg-red-100 text-red-800' };
    case ExecutionStateEnum.SKIPPED:
      // Not a failure — the rule looked and decided no.
      return { label: matched ? tr('AiDispatcher.stateSkipped', 'Skipped') : tr('AiDispatcher.stateNoMatch', 'No match'), className: 'bg-base-tertiary text-content-secondary' };
    case ExecutionStateEnum.PENDING:
    default:
      // The pod died between claiming the rule and recording the outcome. Honest, not a success.
      return { label: tr('AiDispatcher.stateInterrupted', 'Interrupted'), className: 'bg-amber-100 text-amber-800' };
  }
};

/**
 * Every evaluation, not only the ones that acted — the answer to "why didn't my rule fire?".
 *
 * It opens on the rules that matched, so the first thing shown is what the robot did. The
 * `matched: false` rows — considered and declined — are one switch away, and they are the answer
 * when a rule is not firing at all.
 */
const ExecutionLog: React.FC<ComponentProps> = ({ companyId, ruleId }) => {
  const { tr } = useTranslationLgs();
  const { listExecutions, getExecution } = useRuleApi(companyId);
  const { loggedUser } = useContext(AuthenticatedUserContext);
  const triggerLabel = useTriggerLabel();

  // Matched-only by default: the log opens on what the robot actually did. Switch to every
  // evaluation to find out why a rule is not firing — that is where the declined rows are.
  const [onlyMatched, setOnlyMatched] = useState(true);
  const [detail, setDetail] = useState<ExecutionDetailOutDTO>();

  const openDetail = (row: ExecutionListItemOutDTO) => getExecution(row.id, setDetail);

  const columnConfig: ColumnConfig<ExecutionListItemOutDTO>[] = [
    {
      id: 'state',
      header: 'AiDispatcher.columnOutcome',
      headerDefault: 'Outcome',
      width: '9rem',
      verticalAlign: 'middle',
      render: row => {
        const state = stateLabel(tr, row.executionState, row.matched);
        return <span className={`rounded px-2 py-0.5 text-xs font-medium ${state.className}`}>{state.label}</span>;
      },
    },
    {
      id: 'rule',
      header: 'AiDispatcher.ruleName',
      headerDefault: 'Rule',
      verticalAlign: 'middle',
      render: row => <span>{row.ruleName ?? tr('AiDispatcher.deletedRule', 'Deleted rule')}</span>,
    },
    {
      id: 'entity',
      header: 'AiDispatcher.affectedEntity',
      headerDefault: 'Expedition',
      verticalAlign: 'middle',
      render: row => <EntityLink entityType={row.entityType} entityId={row.entityId} entityLabel={row.entityLabel} />,
    },
    {
      id: 'trigger',
      header: 'AiDispatcher.trigger',
      headerDefault: 'Trigger',
      verticalAlign: 'middle',
      render: row => <span>{triggerLabel(row.triggerType) ?? '—'}</span>,
    },
    {
      id: 'createdAt',
      header: 'AiDispatcher.evaluatedAt',
      headerDefault: 'Evaluated',
      verticalAlign: 'middle',
      render: row => <span>{stringDateTimeWithLocaleFromApiStringInTz(row.createdAt, loggedUser?.defaultTimezoneId)}</span>,
    },
    {
      id: 'detail',
      headerRender: () => null,
      width: '10rem',
      verticalAlign: 'middle',
      render: row => (
        <div className="flex justify-end">
          <RingilButton mode="table" label={tr('AiDispatcher.whyLabel', 'Why?')} tooltip={tr('AiDispatcher.whyTooltip', 'Show the condition tree with the values it saw')} onClick={() => openDetail(row)} />
        </div>
      ),
    },
  ];

  const loadDataFromApi = (_filter: unknown, pageable: { pageNumber?: number; pageSize?: number }, onSuccess: (paged: PagedData<ExecutionListItemOutDTO>) => void) => {
    listExecutions(pageable, ruleId, onlyMatched || undefined, data => onSuccess(data));
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <InputSelect
          model={[
            { code: 'MATCHED', label: tr('AiDispatcher.onlyActed', 'Only those that matched') },
            { code: 'ALL', label: tr('AiDispatcher.everyEvaluation', 'Every evaluation') },
          ]}
          selectedItemCode={onlyMatched ? 'MATCHED' : 'ALL'}
          onSelect={code => setOnlyMatched(code === 'MATCHED')}
        />
        {onlyMatched && (
          <span className="text-sm text-content-secondary">
            {tr('AiDispatcher.executionLogHint', 'Not seeing a rule you expected? Switch to every evaluation — it will be there, with the reason it declined.')}
          </span>
        )}
      </div>

      <RingilDataTable
        key={`${ruleId ?? 'all'}-${onlyMatched}`}
        columnConfig={columnConfig}
        loadDataFromApi={loadDataFromApi}
        filterName="aiDispatcherExecutions"
        enableFilterV2={false}
      />

      <Dialog visible={detail !== undefined} onHide={() => setDetail(undefined)} header={tr('AiDispatcher.executionDetail', 'Why this rule did or did not fire')} style={{ width: '48rem', maxWidth: '95vw' }}>
        {detail && (
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-baseline gap-2">
              <span className="font-medium">{detail.ruleName ?? tr('AiDispatcher.deletedRule', 'Deleted rule')}</span>
              <span className="text-content-secondary">{tr('AiDispatcher.onEntity', 'on')}</span>
              <EntityLink entityType={detail.entityType} entityId={detail.entityId} entityLabel={detail.entityLabel} />
              <span className="text-xs text-content-tertiary">{stringDateTimeWithLocaleFromApiStringInTz(detail.createdAt, loggedUser?.defaultTimezoneId)}</span>
            </div>

            {detail.error && <div className="rounded-md border border-solid border-red-200 bg-red-50 p-3 text-sm text-red-700">{detail.error}</div>}

            <section className="flex flex-col gap-2">
              <h4 className="m-0 text-sm font-medium uppercase text-content-secondary">{tr('AiDispatcher.explain', 'Why')}</h4>
              <ExplainTree explain={detail.explain} companyId={companyId} />
            </section>

            <ExecutedActions actions={detail.actions} />
          </div>
        )}
      </Dialog>
    </div>
  );
};

/**
 * `actions` is untyped JSON on the wire and, unlike the feed, arrives in its **stored** shape —
 * the message is an `ActionMessage` enum name rather than a Locize key. Both shapes are handled,
 * so the dialog says what happened instead of only naming the action.
 */
const ExecutedActions: React.FC<{ actions: unknown }> = ({ actions }) => {
  const { tr } = useTranslationLgs();
  if (!Array.isArray(actions) || actions.length === 0) return null;

  return (
    <section className="flex flex-col gap-2">
      <h4 className="m-0 text-sm font-medium uppercase text-content-secondary">{tr('AiDispatcher.whatHappened', 'What happened')}</h4>
      {actions.map((raw, index) => {
        const action = raw as ExecutedAction;
        const detail = executedActionDetail(tr, action);
        const error = executedActionError(tr, action);
        return (
          <div key={index} className={`rounded-md border border-solid p-2 text-sm ${action.ok ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
            <div className="flex flex-wrap items-baseline gap-2">
              <span className={action.ok ? 'text-green-700' : 'text-red-700'}>{action.ok ? '✓' : '✕'}</span>
              <span>{detail ?? action.action}</span>
              {detail && <span className="font-mono text-xs text-content-tertiary">{action.action}</span>}
            </div>
            {error && <div className="mt-1 text-red-700">{error}</div>}
          </div>
        );
      })}
    </section>
  );
};

export default ExecutionLog;
